import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { Carousel } from 'dibs-carousel';
import { useUID } from 'dibs-uid';
import classnames from 'classnames';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';
import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { trackModuleLocation } from '../helpers/moduleLocationTracking';
import { HpRespTopHeroBannerItem } from '../HpRespSharedHeroBanner/HpRespSharedHeroBanner';
import { HpRespTopHeroBannerImageItem } from './HpRespTopHeroBannerImageItem';

import { HpRespTopHeroBannerModule_componentModule as ComponentModule } from './__generated__/HpRespTopHeroBannerModule_componentModule.graphql';

import styles from './HpRespTopHeroBannerModule.scss';

type Props = {
    componentModule: ComponentModule;
    useLazyLoadImages?: boolean;
    pageSetOffset?: boolean;
    isMobile?: boolean;
    totalModules: number;
    moduleIndex: number;
};

export const HpRespTopHeroBannerModuleComponent: FC<Props> = ({
    componentModule,
    useLazyLoadImages,
    totalModules,
    moduleIndex,
}) => {
    const uid = `topHeroBanner-${useUID() || undefined}`;

    if (!componentModule) {
        return null;
    }

    const items = componentModule.messages?.items || [];
    const isFullBleed = !!items.find(item => item?.isFullBleedImage);
    const isFullWidth = !!items.find(item => item?.format === 'CAPTION');
    const itemsPerPage = 1;

    const onItemsImpression = ({
        visibleItems,
        index,
    }: {
        visibleItems: typeof items;
        index: number;
    }): void => {
        const promoTrackingObjects = visibleItems.map(item => {
            return buildPromoTrackingObject({
                id: 'banner-carousel',
                name: 'Banner Carousel',
                item,
                index,
            });
        });
        trackPromoImpressions(promoTrackingObjects);
    };

    const handleClick = (item: typeof items[0], index: number): void => {
        const promoTrackingObject = buildPromoTrackingObject({
            id: 'banner-carousel',
            name: 'Banner Carousel',
            item,
            index,
        });
        trackPromoClick(promoTrackingObject);
        trackModuleLocation({
            label: 'banner carousel module',
            moduleIndex,
            totalModules,
        });
    };

    return (
        <div
            className={classnames(styles.moduleContainer, {
                [styles.fullWidth]: isFullWidth,
                [styles.isFullBleed]: isFullBleed,
            })}
        >
            <HpRespModuleContainer>
                <HpRespCarouselTracking
                    items={items}
                    itemsPerPage={itemsPerPage}
                    onItemsImpression={onItemsImpression}
                >
                    {({ handleIndexChange }) => (
                        <SharedInViewport>
                            {({ inViewport }) => (
                                <Carousel
                                    classNames={{
                                        wrapper: styles.carouselWrapper,
                                        item: styles.carouselItem,
                                    }}
                                    isAutoRun={inViewport && items.length > 1}
                                    dataTn="hero-items-carousel"
                                    showDots={items.length > 1}
                                    hideArrows
                                    totalItems={items.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({
                                        index,
                                        isVisible,
                                    }: {
                                        isVisible: boolean;
                                        index: number;
                                    }) => {
                                        const item =
                                            componentModule?.messages?.items?.[index] || null;
                                        if (!item) return null;
                                        const { format } = item;

                                        return format === 'CAPTION' ? (
                                            <HpRespTopHeroBannerItem
                                                onClick={() => handleClick(items[index], index)}
                                                isVisible={isVisible}
                                                useLazyLoadImages={useLazyLoadImages}
                                                topItem={item}
                                            />
                                        ) : (
                                            <HpRespTopHeroBannerImageItem
                                                handleClick={() => handleClick(items[index], index)}
                                                isVisible={isVisible}
                                                index={index}
                                                useLazyLoadImages={useLazyLoadImages}
                                                bannerItem={item}
                                                itemId={`${uid}-${index}`}
                                            />
                                        );
                                    }}
                                    onIndexChange={handleIndexChange}
                                    renderDot={({
                                        isCurrentDot,
                                    }: { isCurrentDot?: boolean } = {}) => (
                                        <SharedCarouselDot isActive={isCurrentDot} />
                                    )}
                                />
                            )}
                        </SharedInViewport>
                    )}
                </HpRespCarouselTracking>
            </HpRespModuleContainer>
        </div>
    );
};

export const HpRespTopHeroBannerModule = createFragmentContainer(
    HpRespTopHeroBannerModuleComponent,
    {
        componentModule: graphql`
            fragment HpRespTopHeroBannerModule_componentModule on TopHeroBannerModule {
                messages(userType: $userType) {
                    items {
                        format
                        cmsId
                        isFullBleedImage
                        imageUrl
                        linkData {
                            path
                        }
                        ...HpRespTopHeroBannerImageItem_bannerItem
                        ...HpRespSharedHeroBanner_topItem
                    }
                }
            }
        `,
    }
);
